import * as Schema from "effect/Schema";
import { CommandId, ThreadId } from "./baseSchemas.ts";
import { MessageReactionReply } from "./messageReactions.ts";

/** Draft identity is separate from the message identity minted at Send. */
export const MessageQueueReference = Schema.Struct({
  queueId: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(128)),
  expectedRevision: Schema.Number.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(1)),
});

/**
 * Who is holding a held message (GOALS-MESSAGE-QUEUE, words of record):
 * `person` = Paused (the person chose not to send yet), `editing` = the person
 * opened it to change it, `delivery` = Not sent (a stop, an error or a restart
 * returned a scheduled message before Cleo read it). A hold without a reason
 * is a defect: the Desk could not tell Paused from Not sent.
 */
export const QueuedMessageHeldBy = Schema.Literals(["person", "editing", "delivery"]);
export type QueuedMessageHeldBy = typeof QueuedMessageHeldBy.Type;

export const ThreadMessageQueueUpdateCommand = Schema.Struct({
  type: Schema.Literal("thread.message-queue.update"),
  commandId: CommandId,
  threadId: ThreadId,
  queueId: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(128)),
  expectedRevision: Schema.Number.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(0)),
  // `interrupt` = Stop & send: stop the running response and deliver this
  // scheduled message first while the other scheduled messages stay scheduled.
  // `steer` = Steer: deliver this scheduled message into the running response
  // without stopping it, on an engine that reads a message mid-run.
  // `move` = reorder: put this message before `beforeQueueId`, or last when that is absent.
  action: Schema.Literals([
    "add",
    "edit",
    "hold",
    "delete",
    "restore",
    "interrupt",
    "steer",
    "move",
  ]),
  beforeQueueId: Schema.optional(
    Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(128)),
  ),
  text: Schema.optional(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(64000))),
  /** For `hold`: who holds it afterwards. Absent means the person. */
  heldBy: Schema.optional(QueuedMessageHeldBy),
  createdAt: Schema.String,
});

/**
 * A restart pauses the waiting messages instead of sending them (Can 23, PC10:
 * an unsent request is never redirected silently). One record per
 * conversation, `message-queue-pause:<threadId>`: `paused` until the person
 * sends them on with one action, then `resumed`. While it stands nothing
 * waiting is delivered; the person can still edit, move or remove each one.
 */
export const MESSAGE_QUEUE_PAUSE_ACTIVITY_KIND = "message-queue.paused";
export const messageQueuePauseActivityId = (threadId: string) => `message-queue-pause:${threadId}`;

export const MessageQueuePauseRecord = Schema.Struct({
  reason: Schema.Literal("restart"),
  state: Schema.Literals(["paused", "resumed"]),
  /** How many were waiting when the restart paused them. */
  count: Schema.Number.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(0)),
  pausedAt: Schema.String,
  resumedAt: Schema.optional(Schema.String),
});
export type MessageQueuePauseRecord = typeof MessageQueuePauseRecord.Type;

export function readMessageQueuePause(
  activities: ReadonlyArray<{ readonly kind: string; readonly payload: unknown }>,
): MessageQueuePauseRecord | undefined {
  for (let at = activities.length - 1; at >= 0; at--) {
    const activity = activities[at]!;
    if (activity.kind !== MESSAGE_QUEUE_PAUSE_ACTIVITY_KIND) continue;
    const decoded = Schema.decodeUnknownOption(MessageQueuePauseRecord)(activity.payload);
    if (decoded._tag === "Some") return decoded.value;
  }
  return undefined;
}

/** Whether waiting messages must keep waiting for the person's one action. */
export const messageQueueIsPaused = (record: MessageQueuePauseRecord | undefined): boolean =>
  record?.state === "paused";

/** Server only: a restart pauses the conversation's waiting messages. Repeats are no-ops. */
export const ThreadMessageQueuePauseCommand = Schema.Struct({
  type: Schema.Literal("thread.message-queue.pause"),
  commandId: CommandId,
  threadId: ThreadId,
  reason: Schema.Literal("restart"),
  count: Schema.Number.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(0)),
  createdAt: Schema.String,
});
export type ThreadMessageQueuePauseCommand = typeof ThreadMessageQueuePauseCommand.Type;

/** The person's "Send them now": clears the pause and the waiting messages go in order. */
export const ThreadMessageQueueResumeCommand = Schema.Struct({
  type: Schema.Literal("thread.message-queue.resume"),
  commandId: CommandId,
  threadId: ThreadId,
  createdAt: Schema.String,
});
export type ThreadMessageQueueResumeCommand = typeof ThreadMessageQueueResumeCommand.Type;

export const QueuedMessage = Schema.Struct({
  queueId: Schema.String,
  revision: Schema.Number.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(1)),
  text: Schema.String.check(Schema.isMaxLength(64000)),
  state: Schema.Literals(["held", "scheduled", "sent", "deleted"]),
  heldBy: Schema.optional(QueuedMessageHeldBy),
  /** A scheduled message the person asked to deliver first by stopping the running response. */
  interrupt: Schema.optional(Schema.Boolean),
  /** A scheduled message the person asked to deliver into the running response. */
  steer: Schema.optional(Schema.Boolean),
  createdAt: Schema.String,
  updatedAt: Schema.String,
  /**
   * Where the person put it when they reordered the queue. Absent means the
   * place its createdAt gives; see `queueOrderKey`.
   */
  rank: Schema.optional(Schema.Number),
  /**
   * The attachments a held message keeps while it waits (Paused, Not sent,
   * being edited), exactly as it was sent. A scheduled message keeps them in
   * its frozen Send; holding it moves them here so Send puts them back.
   */
  attachments: Schema.optional(Schema.Array(Schema.Unknown)),
  /**
   * Can 46: the fork a queued Send brings back. A scheduled message carries it
   * in its frozen Send; holding it (Paused, Not sent, being edited) moves it
   * here so Send puts it back, as it does the attachments.
   */
  bringBack: Schema.optional(Schema.Struct({ forkThreadId: ThreadId })),
  /**
   * W-77549876: the reply a queued thumbs-up reacts to. A scheduled message
   * carries it in its frozen Send; holding it moves it here so Send puts it
   * back, and the resent thumbs-up is still a reaction on that reply.
   */
  replyReaction: Schema.optional(MessageReactionReply),
  messageId: Schema.optional(Schema.String),
  requestFingerprint: Schema.optional(Schema.String),
  // The frozen command is retained in the event log, never reconstructed
  // from the current composer or selection when its response becomes due.
  scheduledCommand: Schema.optional(Schema.Unknown),
  afterTurnId: Schema.optional(Schema.String),
});
export type QueuedMessage = typeof QueuedMessage.Type;
export const MESSAGE_QUEUE_ACTIVITY_KIND = "message.queued";
export const messageQueueActivityId = (queueId: string) => `message-queue:${queueId}`;
/** Queue identity of a Send that arrived while a response was running (MQ2). */
export const scheduledSendQueueId = (messageId: string) => `send:${messageId}`;

/** A message's place in the queue: its rank when it was moved, else when it was queued. */
export const queueOrderKey = (item: Pick<QueuedMessage, "rank" | "createdAt">): number => {
  if (item.rank !== undefined) return item.rank;
  const at = Date.parse(item.createdAt);
  return Number.isFinite(at) ? at : 0;
};
export const compareQueuedMessages = (
  a: Pick<QueuedMessage, "rank" | "createdAt" | "queueId">,
  b: Pick<QueuedMessage, "rank" | "createdAt" | "queueId">,
): number => queueOrderKey(a) - queueOrderKey(b) || a.queueId.localeCompare(b.queueId);

/** The attachments a queued message will carry when it is sent. */
export function queuedAttachmentsOf(
  item: Pick<QueuedMessage, "attachments" | "scheduledCommand">,
): ReadonlyArray<unknown> {
  if (item.attachments !== undefined) return item.attachments;
  const attachments = (item.scheduledCommand as { message?: { attachments?: unknown } } | undefined)
    ?.message?.attachments;
  return Array.isArray(attachments) ? attachments : [];
}

/** The fork a queued message brings back, wherever the record keeps it. */
export function queuedBringBackOf(
  item: Pick<QueuedMessage, "bringBack" | "scheduledCommand">,
): { readonly forkThreadId: ThreadId } | undefined {
  if (item.bringBack !== undefined) return item.bringBack;
  const forkThreadId = (
    item.scheduledCommand as { bringBack?: { forkThreadId?: unknown } } | undefined
  )?.bringBack?.forkThreadId;
  return typeof forkThreadId === "string" && forkThreadId.length > 0
    ? { forkThreadId: forkThreadId as ThreadId }
    : undefined;
}

/** The reply a queued thumbs-up reacts to, wherever the record keeps it. */
export function queuedReplyReactionOf(
  item: Pick<QueuedMessage, "replyReaction" | "scheduledCommand">,
): MessageReactionReply | undefined {
  if (item.replyReaction !== undefined) return item.replyReaction;
  const frozen = (item.scheduledCommand as { replyReaction?: unknown } | undefined)?.replyReaction;
  return Schema.is(MessageReactionReply)(frozen) ? frozen : undefined;
}

export function readMessageQueue(
  activities: ReadonlyArray<{ readonly kind: string; readonly payload: unknown }>,
): QueuedMessage[] {
  return activities
    .flatMap((activity) => {
      if (activity.kind !== MESSAGE_QUEUE_ACTIVITY_KIND) return [];
      const decoded = Schema.decodeUnknownOption(QueuedMessage)(activity.payload);
      return decoded._tag === "Some" ? [decoded.value] : [];
    })
    .sort(compareQueuedMessages);
}

/** The one word a person sees under a waiting message; null once it is sent or removed. */
export type QueuedMessageStanding = "unread" | "paused" | "not-sent";
export function queuedMessageStanding(
  item: Pick<QueuedMessage, "state" | "heldBy">,
): QueuedMessageStanding | null {
  if (item.state === "scheduled") return "unread";
  if (item.state !== "held") return null;
  return item.heldBy === "delivery" ? "not-sent" : "paused";
}
export const QUEUED_MESSAGE_STANDING_LABEL: Record<QueuedMessageStanding, string> = {
  unread: "Unread",
  paused: "Paused",
  "not-sent": "Not sent",
};

/**
 * W-77549876: whether a stop, an error or a restart was recorded for the
 * conversation's latest turn. Only then is "Cleo stopped before reading" true
 * of a Not sent message; otherwise the Desk did not deliver it, and the message
 * was not the cause. A stopped or errored session counts, as at release, where
 * it holds the waiting messages even after a completed turn. The hold records
 * no cause, so the words follow the conversation as it stands now: a later
 * stop or a later normal turn changes them for an older Not sent message.
 */
export function queueStopRecorded(thread: {
  readonly latestTurn: { readonly turnId: string; readonly state: string } | null | undefined;
  readonly session?:
    | { readonly status: string; readonly lastError?: string | null | undefined }
    | null
    | undefined;
  readonly activities: ReadonlyArray<{ readonly kind: string; readonly turnId?: string | null }>;
}): boolean {
  const turn = thread.latestTurn;
  if (turn?.state === "interrupted" || turn?.state === "error") return true;
  const status = thread.session?.status;
  if (status === "interrupted" || status === "stopped" || status === "error") return true;
  if (thread.session?.lastError) return true;
  return (
    turn != null &&
    thread.activities.some(
      (activity) => activity.kind === "provider.session.lost" && activity.turnId === turn.turnId,
    )
  );
}

/** The note under the Not sent messages, and the words read out for each one. */
export function notSentWords(input: { readonly stopped: boolean; readonly count: number }): {
  readonly note: string;
  readonly label: string;
} {
  if (input.stopped)
    return {
      note:
        input.count === 1
          ? "Cleo stopped before reading your message."
          : `Cleo stopped before reading ${input.count} messages.`,
      label: "Not sent. Cleo stopped before reading this",
    };
  return {
    note:
      input.count === 1
        ? "This message wasn't delivered. Send it again?"
        : `${input.count} messages weren't delivered. Send them again?`,
    label: "Not sent. It wasn't delivered",
  };
}

/**
 * What an engine does with a message handed to it while a response runs, as
 * its adapter declares (`midRunDelivery`): `native` reads it inside the running
 * turn without stopping her tools, `interrupt` stops the turn and reads it
 * first, `none` cannot take one. Unknown is treated as `none`.
 */
type MidRunDelivery = "native" | "interrupt" | "none" | undefined;

/**
 * What a Send does while Cleo works. `queueing` is the person's "Queue
 * messages while Cleo works" preference: on, the message waits as Unread; off,
 * it goes to her now where the engine can take it and waits where it cannot.
 */
export function sendWhileRunningDelivery(input: {
  readonly queueing: boolean;
  readonly midRunDelivery: MidRunDelivery;
}): "after-response" | "now" {
  if (input.queueing) return "after-response";
  return input.midRunDelivery === "native" || input.midRunDelivery === "interrupt"
    ? "now"
    : "after-response";
}

/** The words on the action that hands a message to Cleo mid-run; null when the engine cannot take one. */
export function midRunSendWording(
  midRunDelivery: MidRunDelivery,
): { readonly label: "Steer" | "Stop & send"; readonly hint: string } | null {
  if (midRunDelivery === "native")
    return { label: "Steer", hint: "Cleo reads it now, without stopping" };
  if (midRunDelivery === "interrupt")
    return { label: "Stop & send", hint: "Cleo stops and reads it now" };
  return null;
}

/**
 * Whether a message handed to Cleo mid-run can carry attachments. A steer
 * carries them only where the adapter says so (`midRunAttachments`); a
 * stop-and-send starts an ordinary turn, which carries what any Send does.
 */
export function midRunCarriesAttachments(input: {
  readonly midRunDelivery: MidRunDelivery;
  readonly midRunAttachments: boolean | undefined;
}): boolean {
  if (input.midRunDelivery === "interrupt") return true;
  return input.midRunDelivery === "native" && input.midRunAttachments === true;
}

/**
 * The action that hands one Unread message to Cleo while she works. `steer`
 * joins the running response; `stop-and-send` is the queue's own Stop & send,
 * which every engine has and which carries the message exactly as frozen. A
 * message with attachments steers only where the engine's steer carries them.
 */
export function unreadMidRunAction(input: {
  readonly midRunDelivery: MidRunDelivery;
  readonly midRunAttachments: boolean | undefined;
  readonly attachmentCount: number;
}): {
  readonly action: "steer" | "stop-and-send";
  readonly label: "Steer" | "Stop & send";
  readonly hint: string;
} {
  if (
    input.midRunDelivery === "native" &&
    (input.attachmentCount === 0 || input.midRunAttachments === true)
  )
    return { action: "steer", ...midRunSendWording("native")! };
  return { action: "stop-and-send", ...midRunSendWording("interrupt")! };
}

/** Everything a composer shows and sends for a Send while Cleo works. */
export interface SendWhileRunning {
  readonly delivery: "after-response" | "now";
  /** The word on the Send action. */
  readonly label: "Send" | "Steer" | "Stop & send";
  /** What happens to the message, said beside that word. */
  readonly hint: string;
  /** The empty composer's invitation. */
  readonly placeholder: string;
  /** False when attachments must stay in the composer for this delivery. */
  readonly carriesAttachments: boolean;
}

export function sendWhileRunning(input: {
  readonly queueing: boolean;
  readonly midRunDelivery: MidRunDelivery;
  readonly midRunAttachments: boolean | undefined;
}): SendWhileRunning {
  const wording =
    sendWhileRunningDelivery(input) === "now" ? midRunSendWording(input.midRunDelivery) : null;
  // A queued Send is frozen exactly as sent, attachments included.
  if (wording === null)
    return {
      delivery: "after-response",
      label: "Send",
      hint: "Cleo reads it when she's done",
      placeholder: "Message Cleo. She reads it when she's done.",
      carriesAttachments: true,
    };
  return {
    delivery: "now",
    label: wording.label,
    hint: wording.hint,
    placeholder:
      wording.label === "Steer"
        ? "Message Cleo. She reads it now, without stopping."
        : "Message Cleo. She stops and reads it now.",
    carriesAttachments: midRunCarriesAttachments(input),
  };
}

/** The frozen Send a scheduled message will replay, when its shape is known. */
export function scheduledSendOf(
  item: Pick<QueuedMessage, "scheduledCommand">,
): { readonly messageId: string; readonly attachmentCount: number } | undefined {
  const command = item.scheduledCommand as
    | { message?: { messageId?: unknown; attachments?: unknown } }
    | undefined;
  const messageId = command?.message?.messageId;
  if (typeof messageId !== "string") return undefined;
  const attachments = command?.message?.attachments;
  return { messageId, attachmentCount: Array.isArray(attachments) ? attachments.length : 0 };
}
